import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { type User, UserRole } from 'utils/types';
import { getUserFacingErrorMessage } from 'utils/apiError';
import { authService, type LoginResponse, type RegisterRequest } from 'services/auth.service';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  /** A background refresh failed without invalidating the session. */
  | { type: 'AUTH_REFRESH_FAILED' }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_REFRESH_FAILED':
      // Keep whoever is signed in; only stop the loading state.
      return {
        ...state,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (userData: RegisterRequest, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to map backend customer to frontend User
const mapCustomerToUser = (customer: LoginResponse['customer']): User => {
  // The backend customer object uses cd_statement_name for company name
  // Try to extract name from available fields
  const statementName = customer.cd_statement_name || '';
  const invoiceName = customer.cd_invoice_name || '';
  const nameToUse = invoiceName || statementName || '';
  const nameParts = nameToUse.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    id: customer.cd_id.toString(),
    email: customer.cd_statement_email || customer.cd_invoice_email || '',
    firstName,
    lastName,
    company: customer.cd_statement_name || '',
    phone: customer.cd_statement_telephone || customer.cd_invoice_telephone || undefined,
    role: UserRole.CUSTOMER,
    isVerified: true,
    createdAt: customer.created_at ? (typeof customer.created_at === 'string' ? customer.created_at : customer.created_at.toISOString()) : new Date().toISOString(),
    updatedAt: customer.updated_at ? (typeof customer.updated_at === 'string' ? customer.updated_at : customer.updated_at.toISOString()) : new Date().toISOString(),
    accountCode: customer.cd_ow_account || undefined, // Customer account code for order webhooks
    creditLimit: customer.credit_limit,
    availableToSpend: customer.available_to_spend,
    creditCurrencyCode: customer.credit_currency_code,
    creditCurrencySymbol: customer.credit_currency_symbol,
    balance: customer.balance,
    onHold: Boolean(customer.on_hold),
    // Trades on account. Unlike hasAccountCredit this ignores the hold flag, so
    // an on-hold account can still be told apart from a card-only customer.
    accountCustomer: customer.cd_account_customer === true,
    hasAccountCredit:
      customer.cd_account_customer === true &&
      customer.on_hold !== true &&
      typeof customer.credit_limit === 'number' &&
      customer.credit_limit > 0, // Validation: must have account customer flag, not on hold, and valid credit limit
    // Add statement/delivery address fields for profile and delivery address display
    town: customer.cd_statement_town || undefined,
    country: customer.cd_statement_country || undefined,
    address1: customer.cd_statement_address1 || undefined,
    address2: customer.cd_statement_address2 || undefined,
    address3: customer.cd_statement_address3 || undefined,
    county: customer.cd_statement_county || undefined,
    postcode: customer.cd_statement_postcode || undefined,
  } as User;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing auth token on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = authService.getAccessToken();

      if (token) {
        try {
          dispatch({ type: 'AUTH_START' });
          // Fetch fresh user data from API
          const customerData = await authService.getCurrentUser();
          const user = mapCustomerToUser(customerData);
          dispatch({ type: 'AUTH_SUCCESS', payload: user });
        } catch (error) {
          // If API call fails (token invalid/expired), clear auth data
          authService.clearAuthData();
          dispatch({ type: 'AUTH_FAILURE', payload: 'Session expired' });
        }
      } else {
        // No token found - user is not authenticated (not an error)
        dispatch({ type: 'LOGOUT' });
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = true) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authService.login(email, password, rememberMe);

      // Map backend customer data to frontend User type
      const user = mapCustomerToUser(response.customer);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error) {
      const errorMessage = getUserFacingErrorMessage(error, 'Login failed. Please try again.');
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error; // Re-throw so LoginForm can handle it
    }
  };

  const register = async (userData: RegisterRequest, rememberMe: boolean = true) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authService.register(userData, rememberMe);

      // Map backend customer data to frontend User type
      const user = mapCustomerToUser(response.customer);

      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (error) {
      const errorMessage = getUserFacingErrorMessage(error, 'Registration failed. Please try again.');
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error; // Re-throw so RegisterForm can handle it
    }
  };

  const logout = () => {
    authService.clearAuthData();
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUser = async () => {
    const token = authService.getAccessToken();
    if (token) {
      try {
        dispatch({ type: 'AUTH_START' });
        const customerData = await authService.getCurrentUser();
        const user = mapCustomerToUser(customerData);
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } catch (error) {
        // getCurrentUser clears the stored tokens itself on a 401, so a missing
        // token means the session really is gone. Any other failure (network
        // blip, 5xx) must not sign the customer out mid-session — keep the last
        // known user and leave the stale figures in place until the next read.
        if (authService.getAccessToken()) {
          dispatch({ type: 'AUTH_REFRESH_FAILED' });
          return;
        }
        authService.clearAuthData();
        dispatch({ type: 'AUTH_FAILURE', payload: 'Session expired' });
      }
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 